import {
  ClassroomQueryVariables,
  SinglePostQueryVariables,
} from "@outschool/gql-frontend-generated";
import {
  DataProxy,
  DocumentNode,
  gql,
  useMutation,
} from "@outschool/ui-apollo";
import { ClassPostFragments } from "@outschool/ui-components-classroom";
import React from "react";

import {
  isClassroomQuery,
  isSinglePostQuery,
} from "../../shared/client/hooks/useDeleteClassPostCommentMutation";
import { classroomQuery } from "../routes/Classroom";

export const updateClassPostMutation = gql`
  mutation updateClassPost(
    $classPostUid: ID!
    $messageContentInput: MessageContentInput
    $publishAt: DateTime
    $plainText: Boolean
  ) {
    updateClassPost(
      classPostUid: $classPostUid
      messageContentInput: $messageContentInput
      publishAt: $publishAt
    ) {
      ...ClassPost_classPost
    }
  }
  ${ClassPostFragments.classPost}
`;

export function useUpdateClassPostMutation({
  updateQuery,
  updateQueryVariables,
}: {
  updateQuery: DocumentNode;
  updateQueryVariables: ClassroomQueryVariables | SinglePostQueryVariables;
}) {
  const [mutate] = useMutation(updateClassPostMutation);

  return React.useMemo(
    () =>
      (
        post: { uid: string },
        text: string,
        textMimeType: string,
        attachments: any[],
        video: any,
        publishAt: string,
      ) => {
        const attachmentUids = attachments ? attachments.map(a => a.uid) : [];
        const videoUid = video ? video.uid : null;
        return mutate({
          variables: {
            classPostUid: post.uid,
            messageContentInput: {
              text,
              textMimeType,
              attachmentUids,
              videoUid,
            },
            publishAt,
          },
          update(store, { data: { updateClassPost } }) {
            const data = store.readQuery({
              query: updateQuery,
              variables: updateQueryVariables,
            });
            if (isSinglePostQuery(data)) {
              // Single post page
              const postToUpdate = data.classroom.postByUid;
              if (postToUpdate) {
                store.writeQuery({
                  query: updateQuery,
                  variables: updateQueryVariables,
                  data: {
                    classroom: {
                      ...data.classroom,
                      postByUid: {
                        ...data.classroom.postByUid,
                        ...updateClassPost,
                      },
                    },
                  },
                });
              }
            }

            if (isClassroomQuery(data)) {
              const postToUpdate = data.classroom.posts.find(
                post => post.uid === updateClassPost.uid,
              );
              const unpublishedPostToUpdate =
                data.classroom.unpublishedPosts.find(
                  post => post.uid === updateClassPost.uid,
                );

              if (postToUpdate) {
                store.writeQuery({
                  query: updateQuery,
                  variables: updateQueryVariables,
                  data: {
                    classroom: {
                      ...data.classroom,
                      posts: [
                        ...data.classroom.posts.filter(
                          post => post.uid !== postToUpdate.uid,
                        ),
                        updateClassPost,
                      ],
                    },
                  },
                });
              }

              if (unpublishedPostToUpdate) {
                store.writeQuery({
                  query: updateQuery,
                  variables: updateQueryVariables,
                  data: {
                    classroom: {
                      ...data.classroom,
                      unpublishedPosts: [
                        ...data.classroom.unpublishedPosts.filter(
                          post => post.uid !== unpublishedPostToUpdate.uid,
                        ),
                        updateClassPost,
                      ],
                    },
                  },
                });
              }
            }
          },
        });
      },
    [mutate, updateQuery, updateQueryVariables],
  );
}

export interface UpdateClassroomCacheOptions {
  classPostUid: string;
  newClassPostComment: object;
  classroomQueryVariables?: object;
}

export function updateClassroomCache(
  store: DataProxy,
  {
    newClassPostComment,
    classPostUid,
    classroomQueryVariables,
  }: UpdateClassroomCacheOptions,
) {
  const oldData = store.readQuery({
    query: classroomQuery,
    variables: classroomQueryVariables,
  }) as any;
  const classPostIndex = oldData.classroom.posts.findIndex(
    (p: { uid: string }) => p.uid === classPostUid,
  );
  if (classPostIndex === -1) {
    return;
  }

  const data = {
    ...oldData,
    classroom: { ...oldData.classroom, posts: [...oldData.classroom.posts] },
  };
  const post = { ...data.classroom.posts[classPostIndex] };
  data.classroom.posts[classPostIndex] = post;
  post.commentsPreview = {
    ...post.commentsPreview,
    comments: [...post.commentsPreview.comments, newClassPostComment],
  };

  store.writeQuery({
    query: classroomQuery,
    variables: classroomQueryVariables,
    data,
  });
}
